import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import uniqueId from 'lodash/uniqueId';

import LoadMask from 'components/LoadMask';
import PagePagination from 'components/PagePagination';
import FiltersContentLoader from 'components/FiltersContentLoader';
import LinkButton from 'components/LinkButton';

import { LinkBuilder } from 'helpers/link-builder';
import { useGrid } from 'hooks/useGrid';
import { ENDPOINT } from 'constants/endpoints';
import { ROUTE } from 'constants/route';

import './styles.css';

const ProfileViewed = props => {
  const { isOwnProfile, isNetwork } = props;

  const endpoint =
    isOwnProfile && !isNetwork
      ? ENDPOINT.COLLECTION.PROFILE_VIEW_OWN
      : ENDPOINT.COLLECTION.PROFILE_VIEW_NETWORK;

  const {
    data: {
      isFetching,
      rows,
      rowsCount,
      page,
      perPage,
      TotalViewsCount,
      FullNamesAllowed,
    },
    actions: { readPage, fetch },
  } = useGrid(endpoint, null, false);

  useEffect(() => {
    fetch();
  }, []);

  const renderRecords = useMemo(
    () =>
      rows.map(record => {
        const link = FullNamesAllowed
          ? isOwnProfile
            ? LinkBuilder.getFirmLink(record.FirmUrlAlias)
            : record.ViewProfile === 'Yes'
            ? LinkBuilder.getProfileLink(record.CandidateID)
            : null
          : null;
        return (
          <div className="profile-viewed-block__item" key={uniqueId()}>
            {link ? (
              <NavLink
                to={link}
                target="_blank"
                className="profile-viewed-block__item-title"
              >
                {isOwnProfile ? record.FirmName : record.FullName}
              </NavLink>
            ) : (
              <h5 className="profile-viewed-block__item-title">
                {isOwnProfile ? record.FirmName : record.FullName}
              </h5>
            )}
            <span className="profile-viewed-block__item-views">
              {`${record.ViewsCount} view${
                parseInt(record.ViewsCount) !== 1 ? 's' : ''
              }`}
            </span>
            <span className="profile-viewed-block__item-date">
              {`Latest: ${record.DateView}`}
            </span>
          </div>
        );
      }),
    [rows]
  );

  return !isFetching ? (
    Number(rowsCount) > 0 ? (
      <div className="sidebar__widget">
        {isFetching && <LoadMask relative />}
        <h3 className="profile-viewed-block__title">
          Profile Viewed:{' '}
          <span className="profile-viewed-block__total-views-link">
            {TotalViewsCount}{' '}
            {`time${parseInt(TotalViewsCount) !== 1 ? 's' : ''}`}
          </span>
        </h3>
        <p className="profile-viewed-block__sub-title">
          {isOwnProfile
            ? 'Firms that have viewed your profile.'
            : 'Network user profile views.'}
        </p>
        {!FullNamesAllowed && (
          <p className="profile-viewed-block__sub-title">
            <LinkButton
              to={ROUTE.CANDIDATE_UPGRADE_PREMIUM}
              text="Upgrade To Premium"
              underline={true}
              btnFontWeight={400}
              btnFontSize="0.9286rem"
            />
            {` `}
            to see firm names
          </p>
        )}
        <div className="profile-viewed-block__list">{renderRecords}</div>
        {Number(rowsCount) > Number(perPage) && (
          <div className="profile-viewed-block__pagination">
            <PagePagination
              page={page}
              perPage={perPage}
              count={rowsCount}
              pageRangeDisplayed={3}
              controlFunc={readPage}
              hideFirstLastPages={false}
              hideNavigation={true}
              minimalStyle={true}
              scrollToTop={false}
            />
          </div>
        )}
      </div>
    ) : null
  ) : (
    <FiltersContentLoader />
  );
};

ProfileViewed.propTypes = {
  user: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  isOwnProfile: props =>
    !props.isOwnProfile && !props.isNetwork
      ? new Error(
          `One of props 'isOwnProfile' or 'isNetwork' was not specified in 'ProfileViewed'.`
        )
      : null,
  isNetwork: props =>
    !props.isOwnProfile && !props.isNetwork
      ? new Error(
          `One of props 'isOwnProfile' or 'isNetwork' was not specified in 'ProfileViewed'.`
        )
      : null,
};

export default ProfileViewed;
